import { z } from 'zod';
// Same three values as feed-card.ts's conceptFormatSchema. Inlined
// to avoid a circular import (feed-card.ts adds the drop payload
// to its discriminated union, so it imports from us). Keep these
// in lockstep — three formats, never grows without coordination.
const dropConceptFormatSchema = z.enum(['static_image', 'video', 'carousel']);
// Creative Drops (s214). Spec §6 — twice-weekly cadence:
//   Tuesday 11am · Drop #1 — 3-5 concepts, each with a hypothesis,
//     pre-flight scores, and a per-concept launch decision derived
//     from the brand's authority map.
//   Thursday 11am · Drop #2 — iterations on Tuesday's winners (or
//     fresh hypotheses if Tuesday's are dead).
//
// The drop card is a batch presentation, not a single ask. Each
// concept lives inside the payload — we deliberately don't fan
// each one out into its own concept_proposal card because the
// drop is the agency-feel moment ("here's this week's slate").
// The downstream production / launch pipeline still runs through
// the existing concept agent + creative pipeline when the founder
// (or A0 trust) gives the go-ahead.
/** Pre-flight scores attached to each concept the drop proposes.
 *  Both fields are 0..100 normalized — easy to render as bars
 *  without per-metric formatting in the UI. */
export const dropPreflightSchema = z.object({
    /** Brand-brain fit — how well the concept aligns with positioning,
     *  voice, and ICP segments. Higher = tighter fit. */
    brandBrainFit: z.number().min(0).max(100),
    /** LLM-critic ship-readiness — the critic's score for whether
     *  this concept would pass review today. Higher = closer to ship. */
    criticScore: z.number().min(0).max(100),
});
/** Per-concept launch decision derived from the brand's authority
 *  map for 'creative_direction'. Mag's stance on the concept given
 *  current trust:
 *    - auto_launch: A0, Mag will run it; surfaced for transparency
 *    - auto_launch_unless_objection: A1, runs after deadline if quiet
 *    - needs_approval: A2/A3, founder must explicitly approve */
export const dropProposedDecisionSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('auto_launch') }),
    z.object({
        kind: z.literal('auto_launch_unless_objection'),
        /** ISO timestamp the auto-launch fires if no founder action. */
        deadlineIso: z.string(),
    }),
    z.object({ kind: z.literal('needs_approval') }),
]);
/** One concept inside the drop. Same field shape as a standalone
 *  concept_proposal payload (hook/angle/format/rationale) plus the
 *  drop-specific extras: an explicit hypothesis the founder can
 *  graded against, pre-flight scores, and the proposed decision. */
export const dropConceptSchema = z.object({
    /** A stable id so per-concept actions (approve / object) target
     *  the right concept without index ambiguity. The runner assigns
     *  uuids; the agent never sees them. */
    id: z.string().uuid(),
    hook: z.string().min(1).max(140),
    angle: z.string().min(1).max(200),
    format: dropConceptFormatSchema,
    /** The testable hypothesis — "if we lead with the founder face on
     *  story placements, watch-time-to-CTA rises by 10%+". Single short
     *  sentence; this is what makes the drop fall-line falsifiable
     *  rather than a wish list. */
    hypothesis: z.string().min(1).max(240),
    /** Why this concept right now — ties to brain facts or recent
     *  performance. Mag voice. */
    rationale: z.string().min(1).max(600),
    preflight: dropPreflightSchema,
    proposedDecision: dropProposedDecisionSchema,
    /** s219 — pre-rendered preview PNG URL. Set by the drop runner
     *  after kicking off a brand-aligned canvas template render via
     *  runCreativePipeline (campaignId=null). Null when:
     *   - render failed or timed out (drop ships text-only on that
     *     concept; the founder still sees hook + rationale)
     *   - the concept format is 'video' (s220 wires the Remotion
     *     path; until then video concepts render no preview) */
    previewImageUrl: z.string().url().nullable().optional(),
    /** The creative_assets row id backing previewImageUrl. Lets the
     *  dashboard deep-link to manage / regenerate the preview and
     *  lets future cleanup find drop-preview rows. */
    previewAssetId: z.string().uuid().nullable().optional(),
    /** s270 — full ordered list of carousel slide PNG URLs (slide 0 is
     *  the cover, same URL as previewImageUrl). Only populated for
     *  format === 'carousel' when ALL slides rendered within the drop's
     *  timeout; the dashboard renders these as a swipeable IG carousel
     *  with dot indicators. Empty/absent for static + video (the single
     *  previewImageUrl is the whole preview) and for carousels where one
     *  or more slides failed/timed out — those degrade to a cover-only
     *  preview rather than show a half-rendered carousel. */
    previewSlideUrls: z.array(z.string().url()).optional(),
    /** s222 (Phase A of duration intelligence). Mag's chosen render
     *  duration in seconds for video concepts. Range 4-12, integer.
     *  Only set when format === 'video'; ignored on static/carousel.
     *  Absent → render worker falls back to the template meta default.
     *  See the creative duration intelligence doc (CommitMind). */
    durationSeconds: z.number().int().min(4).max(12).nullable().optional(),
    /** s222 — one-line Mag-voice reasoning for the duration choice.
     *  Surfaced in the dashboard so the founder can see why Mag picked
     *  4s vs 8s. Cap kept short — this is a tag, not a paragraph. */
    durationReasoning: z.string().min(1).max(140).nullable().optional(),
    /** s224 — Mag's chosen aspect for video concepts. Maps to a
     *  CreativeAssetFormat:
     *    'story_9x16' → story_9x16_video (1080×1920, full vertical)
     *    'feed_1x1'   → feed_1x1_video   (1080×1080, square feed)
     *    'feed_4x5'   → feed_4x5_video   (1080×1350, portrait feed)
     *  Only set when format === 'video'; ignored otherwise. Runner
     *  defaults to 'story_9x16' when missing on a video concept. */
    videoAspect: z.enum(['story_9x16', 'feed_1x1', 'feed_4x5']).nullable().optional(),
    /** s233.24 — phase B template router. Which template Mag picked
     *  for this concept's preview render + her one-line rationale.
     *  Surfaces on the drop card so the founder reads WHY this look.
     *  Null when the preview render skipped or crashed before the
     *  router could pick. */
    templateKey: z.string().min(1).max(60).nullable().optional(),
    templateRationale: z.string().min(1).max(140).nullable().optional(),
});
export const creativeDropPayloadSchema = z.object({
    kind: z.literal('creative_drop'),
    /** Which drop in the week — 1 = Tuesday, 2 = Thursday. Stored
     *  rather than re-derived from the date so the renderer doesn't
     *  carry weekday logic. */
    dropNumber: z.union([z.literal(1), z.literal(2)]),
    /** ISO date the drop is for (in brand tz). Per-brand-per-week
     *  dedup key together with dropNumber. */
    dropDate: z.string(),
    /** Brand's tz the schedule fired in. */
    tz: z.string().min(1).max(60),
    /** Short Mag-voice paragraph framing the drop ("here's this week's
     *  slate — three angles, one i'd run hard"). */
    intro: z.string().min(1).max(400),
    /** 3-5 concepts. Empty arrays are rejected — a drop with no
     *  concepts is a non-event, the runner skips it entirely. */
    concepts: z.array(dropConceptSchema).min(3).max(5),
    /** Optional single question for the founder. Null when there's
     *  nothing worth pulling them in on. */
    closeQuestion: z.string().min(1).max(280).nullable(),
    /** s233 — frozen snapshot of the audience-video-signal Mag was
     *  reading when she drafted this drop. Surfaces in the dashboard
     *  as a "what i'm reading · refreshed Xh ago" attribution block
     *  between the intro and the concepts. Captured at drop creation
     *  (not re-derived at render) so the line + staleness stay aligned
     *  with the actual reasoning the agent saw.
     *
     *  Null when the brand had no usable signal (cold-start or thin
     *  history) — renderer emits no block; the agent's per-concept
     *  rationale already covers the absence in voice. Optional on the
     *  schema so feed cards drafted before s233.1 still validate. */
    audienceVideoSignal: z
        .object({
        /** Same string fed into the agent prompt. Founder reads it
         *  verbatim — "last 90d on instagram: 8 reels..." */
        line: z.string().min(1).max(400),
        /** Posts that contributed to the aggregate. Surfaces nowhere
         *  in the chip itself (the line already says "signal from N
         *  posts"); kept for future analytics + the "refreshed Xh ago"
         *  freshness label calc. */
        postsAnalyzed: z.number().int().min(0),
        /** ISO timestamp the aggregate was computed. Drives the
         *  "refreshed Xh ago" relative label. */
        generatedAt: z.string(),
    })
        .nullable()
        .optional(),
});
/** What the agent returns. Concept ids + preflight scores + proposed
 *  decision are stamped by the runner (not the LLM) — the agent only
 *  authors the editorial fields. */
export const creativeDropAgentConceptSchema = z.object({
    hook: z.string().min(1).max(140),
    angle: z.string().min(1).max(200),
    format: dropConceptFormatSchema,
    hypothesis: z.string().min(1).max(240),
    rationale: z.string().min(1).max(600),
    /** s222 (Phase A) — agent picks duration for video concepts.
     *  Required when format === 'video', omitted otherwise. The
     *  runner enforces the "video iff duration" invariant + defaults
     *  to 4s if missing on a video concept. */
    durationSeconds: z.number().int().min(4).max(12).optional(),
    durationReasoning: z.string().min(1).max(140).optional(),
    /** s224 — agent picks the placement aspect for video concepts.
     *  Required when format === 'video'; omitted otherwise. Runner
     *  defaults to 'story_9x16' on missing. */
    videoAspect: z.enum(['story_9x16', 'feed_1x1', 'feed_4x5']).optional(),
});
export const creativeDropAgentOutputSchema = z.object({
    intro: z.string().min(1).max(400),
    concepts: z.array(creativeDropAgentConceptSchema).min(3).max(5),
    closeQuestion: z.string().min(1).max(280).nullable(),
});
//# sourceMappingURL=creative-drop.js.map